/**
 * Admin Canned Responses Management Page
 * Manage template responses for support tickets
 */

import React, { useState, useEffect } from 'react';
import {
  MessageSquare,
  Search,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  Copy,
  Eye,
  Tag,
  Clock,
} from 'lucide-react';

interface CannedResponse {
  id: number;
  name: string;
  shortcode: string;
  content: string;
  departmentId?: number;
  departmentName?: string;
  useCount: number;
  lastUsed?: string;
  createdAt: string;
  updatedAt: string;
}

const CannedResponsesPage: React.FC = () => {
  const [responses, setResponses] = useState<CannedResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDepartment, setFilterDepartment] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingResponse, setEditingResponse] = useState<CannedResponse | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  useEffect(() => {
    loadResponses();
  }, []);

  const loadResponses = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/admin/canned-responses', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setResponses(data.data.responses);
      }
    } catch (error) {
      console.error('Failed to load canned responses:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredResponses = responses.filter((response) => {
    const searchMatch =
      searchQuery === '' ||
      response.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      response.shortcode.toLowerCase().includes(searchQuery.toLowerCase()) ||
      response.content.toLowerCase().includes(searchQuery.toLowerCase());

    const departmentMatch =
      filterDepartment === 'all' ||
      (filterDepartment === 'global' && !response.departmentId) ||
      response.departmentId?.toString() === filterDepartment;

    return searchMatch && departmentMatch;
  });

  const handleCopyShortcode = (shortcode: string, id: number) => {
    navigator.clipboard.writeText(`/${shortcode}`);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDeleteResponse = async (responseId: number) => {
    if (!confirm('Are you sure you want to delete this canned response?')) return;

    try {
      await fetch(`/api/v1/admin/canned-responses/${responseId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      loadResponses();
    } catch (error) {
      console.error('Failed to delete canned response:', error);
      alert('Failed to delete canned response');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return formatDate(dateString);
  };

  const globalResponses = responses.filter((r) => !r.departmentId).length;
  const totalUses = responses.reduce((sum, r) => sum + r.useCount, 0);
  const mostUsed = responses.length > 0 ? Math.max(...responses.map((r) => r.useCount)) : 0;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <MessageSquare className="w-8 h-8" />
            Canned Responses
          </h1>
          <p className="text-slate-400 mt-2">
            Create template responses for faster support ticket replies
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2 font-medium"
        >
          <Plus className="w-5 h-5" />
          New Response
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Templates</p>
              <p className="text-2xl font-bold text-white mt-1">{responses.length}</p>
            </div>
            <MessageSquare className="w-10 h-10 text-orange-400" />
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Global Templates</p>
              <p className="text-2xl font-bold text-purple-400 mt-1">{globalResponses}</p>
            </div>
            <Tag className="w-10 h-10 text-purple-400" />
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Uses</p>
              <p className="text-2xl font-bold text-green-400 mt-1">{totalUses}</p>
            </div>
            <RefreshCw className="w-10 h-10 text-green-400" />
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Most Used</p>
              <p className="text-2xl font-bold text-yellow-400 mt-1">{mostUsed}</p>
            </div>
            <Clock className="w-10 h-10 text-yellow-400" />
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, shortcode, or content..."
            className="w-full pl-10 pr-4 py-3 bg-slate-800/80 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
          />
        </div>

        <select
          value={filterDepartment}
          onChange={(e) => setFilterDepartment(e.target.value)}
          className="px-4 py-3 bg-slate-800/80 border border-slate-700/50 rounded-lg text-white focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
        >
          <option value="all">All Departments</option>
          <option value="global">Global (All Departments)</option>
        </select>
      </div>

      {/* Responses List */}
      {loading ? (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-12 border border-slate-700/50">
          <div className="flex items-center justify-center">
            <RefreshCw className="w-8 h-8 text-orange-400 animate-spin" />
            <span className="ml-3 text-slate-400">Loading canned responses...</span>
          </div>
        </div>
      ) : filteredResponses.length === 0 ? (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-12 border border-slate-700/50 text-center">
          <MessageSquare className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">No Canned Responses Found</h3>
          <p className="text-slate-400">
            {searchQuery
              ? 'No responses match your search.'
              : 'Create your first canned response to speed up ticket replies.'}
          </p>
          {!searchQuery && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-6 px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors inline-flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Create Canned Response
            </button>
          )}
        </div>
      ) : (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="text-left p-4 text-slate-300 font-medium">Name</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Shortcode</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Department</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Preview</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Usage</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Last Used</th>
                  <th className="text-right p-4 text-slate-300 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {filteredResponses.map((response) => (
                  <tr key={response.id} className="hover:bg-slate-700/30">
                    <td className="p-4">
                      <div className="font-medium text-white">{response.name}</div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <code className="px-2 py-1 bg-slate-900 text-orange-400 rounded font-mono text-sm">
                          /{response.shortcode}
                        </code>
                        <button
                          onClick={() => handleCopyShortcode(response.shortcode, response.id)}
                          className="p-1 text-slate-400 hover:text-orange-400 transition-colors"
                          title="Copy shortcode"
                        >
                          {copiedId === response.id ? (
                            <span className="text-green-400 text-xs">Copied!</span>
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="p-4">
                      {response.departmentName ? (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-orange-900/20 text-orange-400">
                          {response.departmentName}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-900/20 text-purple-400">
                          Global
                        </span>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="text-slate-400 text-sm max-w-xs truncate">
                        {response.content}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-white font-medium">{response.useCount}</span>
                      <span className="text-slate-400 text-sm ml-1">times</span>
                    </td>
                    <td className="p-4 text-slate-300 text-sm">
                      {response.lastUsed ? formatRelativeTime(response.lastUsed) : 'Never'}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setEditingResponse(response)}
                          className="p-2 text-orange-400 hover:bg-slate-700/50 rounded transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteResponse(response.id)}
                          className="p-2 text-red-400 hover:bg-slate-700/50 rounded transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Modal Placeholder */}
      {(showCreateModal || editingResponse) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-white mb-4">
              {editingResponse ? 'Edit Canned Response' : 'Create Canned Response'}
            </h2>
            <p className="text-slate-400 mb-6">
              Form will include fields for name, shortcode (e.g., "welcome"), content (rich text
              editor), and department assignment. Staff can use the shortcode in ticket replies by
              typing /{'{'}shortcode{'}'}.
            </p>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Response Name
                </label>
                <input
                  type="text"
                  placeholder="Welcome Message"
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700/50 rounded-lg text-white focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Shortcode
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-slate-400">/</span>
                  <input
                    type="text"
                    placeholder="welcome"
                    className="flex-1 px-4 py-2 bg-slate-900 border border-slate-700/50 rounded-lg text-white focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Use lowercase letters, numbers, and hyphens only
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Response Content
                </label>
                <textarea
                  rows={6}
                  placeholder="Thank you for contacting us..."
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700/50 rounded-lg text-white focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Department (Optional)
                </label>
                <select className="w-full px-4 py-2 bg-slate-900 border border-slate-700/50 rounded-lg text-white focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20">
                  <option value="">Global (Available to all departments)</option>
                  <option value="1">Sales</option>
                  <option value="2">Support</option>
                  <option value="3">Billing</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingResponse(null);
                }}
                className="px-4 py-2 bg-slate-700/50 text-white rounded-lg hover:bg-slate-600/50 transition-colors"
              >
                Cancel
              </button>
              <button className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors">
                {editingResponse ? 'Save Changes' : 'Create Response'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CannedResponsesPage;
